// src/store/reducers/reducerHelpers/thunkPost.ts
import { axiosClientNoCache, csrf } from '@lib/axiosClientCached.ts';
import { processStoreError } from '@lib/errorHandler'; // Import the error processor
import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectCurrentCompanySlug } from '@store/reducers/meReducer.ts';
import { addErrorResponseNotification } from '@store/reducers/notificationReducer.ts';
import {
  type InterfaceFunctionReplace,
  type InterfaceVarReplace,
  replaceUrlState,
  replaceUrlVariable,
  safeDispatchActions,
} from '@store/reducers/reducerHelpers/thunkHelperShared.ts';

import type { DispatchActionType } from '@/types/DispatchActionType.ts';

export interface InterfaceThunkPostData<T = undefined, U = undefined | InterfaceVarReplace> {
  form?: T;
  urlVarsReplaceMap?: U;
}

const genericTypeThunkPostReplace = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createAsyncThunk(
    `${type}/post/${url}`,
    async (data: InterfaceThunkPostData<T, U> | undefined, { rejectWithValue, dispatch, getState }) => {
      let fullUrl = replaceUrlState(url, replaceMap, getState);
      if (data?.urlVarsReplaceMap !== undefined) {
        fullUrl = replaceUrlVariable(fullUrl, data.urlVarsReplaceMap ?? {});
      }
      await csrf();
      const res = await axiosClientNoCache.post(fullUrl, data?.form).catch((error) => {
        dispatch(addErrorResponseNotification({ error, rejectWithValue: undefined })); // rejectWithValue not used by notification here
        safeDispatchActions(dispatch, errorDispatchArray);
        const processedError = processStoreError(error);
        throw rejectWithValue({
          error: {
            message: processedError.message,
            statusCode: processedError.statusCode,
            data: processedError.data,
          },
        });
      });
      safeDispatchActions(dispatch, successDispatchArray);
      return res.data.data;
    },
  );
const genericTypeThunkPost = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) => genericTypeThunkPostReplace<T, U>(type, url, {}, successDispatchArray, errorDispatchArray);

const genericTypeThunkPostCompany = <T = undefined, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  genericTypeThunkPostReplace<T, U>(
    type,
    url,
    {
      '{companySlug}': selectCurrentCompanySlug,
    },
    successDispatchArray,
    errorDispatchArray,
  );

export const thunkPost = {
  replUrl: genericTypeThunkPostReplace,
  compUrl: genericTypeThunkPostCompany,
  post: genericTypeThunkPost,
};

export default thunkPost;
